import { Avatar, Box, Grid, Hidden, SelectChangeEvent, Skeleton, SxProps, Theme, Typography } from "@mui/material";
import { CommonLayout } from "./commonComponents";
import BreadCrum from "../lib/BreadCrum";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../redux-store/reducers";
import { useState, useEffect } from "react";
import { FamilyDetailsData, GetFamilyDetailsResponseBody } from "../redux-store/types/investorProfile";
import { getFamilyDetails } from "../redux-store/actions/investorProfile";
import dayjs from "../utils/dayjs";
import { formatCurrency, isModuleAllowed, maskInfo } from "../utils/utilityFunctions";
import PieChartComponent from "./investorOverview/directEquity/directEquityOverview/PieChartComponent";
import FamilyPieChartComponent from "./investorOverview/common/FamilyPieChart";

export interface navigationData {
    clientCode?: string;
    familyName?: string;
    accountFilter?: number;
}


const FamilyInfo = ({
    columnWidth = { xs: 4 },
    personName,
    isAdmin,
    personDesignation,
    investmentValue,
    currentValue,
    overallReturns,
    irr,
    loading,
    handleNavigation,
    clientCode,
    investorType,
    isFamilyHead
}: {
    columnWidth?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
    personName: string;
    isAdmin?: boolean;
    personDesignation: string | null;
    investmentValue: number;
    currentValue: number;
    overallReturns: number;
    irr: number;
    loading: boolean,
    handleNavigation?: (clientCode: string, investorName: string) => void,
    clientCode?: string;
    investorType?: string;
    isFamilyHead?: string;
}): JSX.Element => {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {!loading && (
                <Grid
                    xs={columnWidth?.xs ?? false}
                    sm={columnWidth?.sm ?? false}
                    md={columnWidth?.md ?? false}
                    lg={columnWidth?.lg ?? false}
                    xl={columnWidth?.xl ?? false}
                    item
                    container
                    sx={(theme: Theme) => ({
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                        borderRadius: "12px",
                        padding: "24px",
                        flexDirection: "column"
                    })}
                >
                    <Grid
                        item
                        container
                        sx={{
                            alignItems: "center",
                            flexWrap: "nowrap",
                            columnGap: "12px",
                            paddingBottom: "20px"
                        }}
                    >
                        <Grid item>
                            <Avatar
                                sx={(theme: Theme) => ({
                                    borderRadius: "12px",
                                    backgroundColor: theme.palette.grey[50],
                                    padding: { xs: "15px", sm: "18px", md: "20px", lg: "25px" },
                                    boxShadow: `0px 3.5px 5.5px 0px ${theme.palette.common.black}05`
                                })}
                            >
                                <Typography
                                    variant="body_600"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.primary[500],
                                        lineHeight: "22.5px"
                                    })}
                                >
                                    {personName.slice(0, 2).toUpperCase()}
                                </Typography>
                            </Avatar>
                        </Grid>
                        <Grid
                            item
                            container
                            xs={10}
                            sx={{
                                flexDirection: "column",
                                flexWrap: "nowrap",
                                rowGap: "3px"
                            }}
                        >
                            <Grid
                                xs
                                item
                                container
                                sx={{
                                    columnGap: "5px",
                                    flexWrap: "wrap",
                                    alignItems: "center"
                                }}
                            >
                                <Grid item sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                                    <Typography
                                        variant="body_600"
                                        onClick={() => handleNavigation ? handleNavigation(clientCode as string, personName) : null}
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.primary[500],
                                            lineHeight: "22.5px",
                                            cursor: "pointer"
                                        })}
                                    >
                                        {personName}
                                    </Typography>
                                    {isAdmin && (
                                        <Grid
                                            item
                                            sx={(theme: Theme) => ({
                                                borderRadius: "8px",
                                                padding: "3px 10px",
                                                backgroundColor: theme.palette.grey[50]
                                            })}
                                        >
                                            <Typography
                                                variant="overline_400"
                                                sx={(theme: Theme) => ({
                                                    fontWeight: 500,
                                                    lineHeight: "15px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.primary[500]
                                                })}
                                            >
                                                Admin
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    flexWrap: "wrap",
                                }}
                            >
                                {personDesignation && (
                                    <Typography
                                        variant="subtitle_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "17.5px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.secondary
                                        })}
                                    >
                                        {personDesignation || "-"}
                                    </Typography>
                                )}
                                {investorType && (
                                    <>
                                        <Box
                                            sx={{
                                                width: "3px",
                                                height: "3px",
                                                backgroundColor: "text.secondary",
                                                alignSelf: "center",
                                                borderRadius: "50%"
                                            }}
                                        />
                                        <Typography
                                            variant="subtitle_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "17.5px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.secondary
                                            })}
                                        >
                                            {investorType || "-"}
                                        </Typography>
                                    </>
                                )}
                                {(`${isFamilyHead ?? ""}`.toLowerCase() === "y") && (
                                    <>
                                        <Box
                                            sx={{
                                                width: "3px",
                                                height: "3px",
                                                backgroundColor: "text.secondary",
                                                alignSelf: "center",
                                                borderRadius: "50%"
                                            }}
                                        />
                                        <Typography
                                            variant="subtitle_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "17.5px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.secondary
                                            })}
                                        >
                                            Head of Family
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "left",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.customColors.greyFourty
                                })}
                            >
                                Investment Value
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "right",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.04px",
                                    color: theme.palette.customColors.codGrey
                                })}
                            >
                                {formatCurrency({ value: investmentValue, locale, currencyCode })}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "left",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.customColors.greyFourty
                                })}
                            >
                                Current Value
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "right",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.04px",
                                    color: theme.palette.customColors.codGrey
                                })}
                            >
                                {formatCurrency({ value: currentValue, locale, currencyCode })}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "left",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.customColors.greyFourty
                                })}
                            >
                                Overall Returns
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "right",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.04px",
                                    color: overallReturns < 0 ? theme.palette.error[500] : theme.palette.success[800]
                                })}
                            >
                                {formatCurrency({ value: overallReturns, locale, currencyCode })}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingTop: "12px",
                        }}
                    >
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "left",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.customColors.greyFourty
                                })}
                            >
                                Absolute Return
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "right",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.04px",
                                    color: irr < 0 ? theme.palette.error[500] : theme.palette.success[800]
                                })}
                            >
                                {irr?.toFixed(1)}%
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            )}
            {loading && (
                <Grid
                    {...{ columnWidth }}
                    item
                    container
                    sx={(theme: Theme) => ({
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                        borderRadius: "12px",
                        padding: "24px",
                        flexDirection: "column"
                    })}
                >
                    <Grid
                        item
                        container
                        sx={{
                            alignItems: "center",
                            flexWrap: "nowrap",
                            columnGap: "12px",
                            paddingBottom: "20px"
                        }}
                    >
                        <Grid item>
                            <Skeleton animation="wave" variant="circular" sx={{ width: "50px", height: "50px" }} />
                        </Grid>
                        <Grid
                            item
                            container
                            xs={10}
                            sx={{
                                flexDirection: "column",
                                flexWrap: "nowrap",
                                rowGap: "3px"
                            }}
                        >
                            <Grid
                                xs
                                item
                                container
                                sx={{
                                    columnGap: "5px",
                                    flexWrap: "wrap",
                                    alignItems: "center"
                                }}
                            >
                                <Grid item sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                                    <Skeleton animation="wave" width={'80px'} />
                                    {isAdmin && (
                                        <Grid
                                            item
                                            sx={(theme: Theme) => ({
                                                borderRadius: "8px",
                                                padding: "3px 10px",
                                                backgroundColor: theme.palette.grey[50]
                                            })}
                                        >
                                            <Skeleton animation="wave" width={'50px'} />
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                            <Grid xs item>
                                <Skeleton animation="wave" width={'50%'} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Skeleton animation="wave" width={'70%'} />
                        </Grid>
                        <Grid item xs={6}>
                            <Skeleton animation="wave" />
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Skeleton animation="wave" width={'70%'} />
                        </Grid>
                        <Grid item xs={6}>
                            <Skeleton animation="wave" />
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Skeleton animation="wave" width={'70%'} />
                        </Grid>
                        <Grid item xs={6}>
                            <Skeleton animation="wave" />
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingTop: "12px",
                        }}
                    >
                        <Grid item xs={6}>
                            <Skeleton animation="wave" width={'70%'} />
                        </Grid>
                        <Grid item xs={6}>
                            <Skeleton animation="wave" />
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </>
    )
}

export default function FamilyProfileDetails(): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch()

    const { pathname: pathName, state } = history.location;
    const navState = state as navigationData

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id)
    const { businessDate } = useSelector((state: RootStateType) => state.navbarInfo)
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);

    const [familyData, setFamilyData] = useState<FamilyDetailsData>({} as FamilyDetailsData)
    const [isFamilyDataLoading, setFamilyDataLoading] = useState<boolean>(false)
    const [open, setOpen] = useState(false);
    const [apiError, setApiError] = useState<boolean>(true);
    const [isSuccess, setIsSuccess] = useState<boolean>(true);


    useEffect(() => {
        const fetchData = async () => {
            try {
                setFamilyDataLoading(true)

                const response = await (dispatch(getFamilyDetails({
                    familyName: navState?.familyName ?? "",
                    rmId: `${iwappRmId}`
                })) as unknown as Promise<GetFamilyDetailsResponseBody>)

                if (response?.isSuccess) {
                    const modifiedRes: FamilyDetailsData = {
                        ...response.result,
                        familyName: maskInfo(response.result?.familyName),
                        familyMembers: response.result?.familyMembers?.map((item: any) => ({
                            ...item,
                            name: maskInfo(item.name)
                        })),
                        familyDistribution: {
                            ...response.result?.familyDistribution,
                            labelData: response.result?.familyDistribution?.labelData?.map((item: any) => ({
                                ...item,
                                label: maskInfo(item.label)
                            }))
                        }

                    }

                    // console.log('modifiedRes', modifiedRes)
                    setFamilyData(modifiedRes)
                    setApiError(false)
                    setIsSuccess(true)
                } else {
                    setApiError(true)
                    setIsSuccess(false)
                }


            } catch (error) {
                setApiError(true)
                setIsSuccess(false)
                console.log("Error: ", error)
            } finally {
                setFamilyDataLoading(false)
            }
        }


        fetchData()
    }, [])


    const initialRouter = pathName.split('/').filter((item) => item !== '')

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            state: {
                accountFilter: navState?.accountFilter,
            }
        },
        {
            name: `${initialRouter[1]}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode
            }
        },
    ] : [])

    const handleClickEvent = (clientCode: string, investorName: string) => {
        history.push(`/investor-profile/${investorName}`, {
            clientCode: clientCode,
            portfoiloId: "",
        })
    }

    return (
        <CommonLayout>
            <>
                <BreadCrum array={routerPath} />
                <Hidden lgDown>
                    {!isFamilyDataLoading &&
                        <Grid
                            container
                            sx={{
                                marginTop: "25px",
                                flexWrap: "nowrap",
                                columnGap: "25px"
                            }}
                        >
                            <Grid
                                xs={8}
                                item
                                container
                                sx={{
                                    flexDirection: "column",
                                    rowGap: "25px",

                                }}
                            >
                                <Grid
                                    item
                                    container
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                        padding: "35px",
                                        borderRadius: "25px",
                                        flexWrap: "nowrap",
                                        justifyContent: "space-between",

                                    })}
                                >
                                    <Grid
                                        xs={6}
                                        item
                                        container
                                        sx={{
                                            columnGap: "15px",
                                            alignItems: "flex-start",
                                            flexWrap: "nowrap",
                                            wordBreak: "break-word"
                                        }}
                                    >
                                        <Grid item>
                                            <Avatar
                                                sx={(theme: Theme) => ({
                                                    backgroundColor: theme.palette.primary[50],
                                                    padding: { xs: "15px", sm: "18px", md: "20px", lg: "25px" }
                                                })}
                                            >
                                                <Typography
                                                    variant="h2"
                                                    sx={(theme: Theme) => ({
                                                        color: theme.palette.primary[500]
                                                    })}
                                                >
                                                    {familyData?.familyName?.[0]}
                                                </Typography>
                                            </Avatar>
                                        </Grid>
                                        <Grid
                                            item
                                            container
                                            sx={{
                                                flexDirection: "column",
                                                rowGap: "3px",
                                                flexWrap: "nowrap"
                                            }}
                                        >
                                            {(apiError && !isSuccess) && <Typography variant="body_500">Something is went wrong</Typography>}
                                            <Grid item>
                                                <Typography
                                                    variant="h3"
                                                    sx={(theme: Theme) => ({
                                                        lineHeight: "32.5px",
                                                        color: theme.palette.text.primary
                                                    })}
                                                >
                                                    {familyData?.familyName?.split(" ")[0]}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography
                                                    variant="body_500"
                                                    sx={(theme: Theme) => ({
                                                        lineHeight: "20px",
                                                        letterSpacing: "0.02px",
                                                        color: theme.palette.text.secondary
                                                    })}
                                                >
                                                    {familyData?.familyName?.split(" ")[1] || ''}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {/* <Grid xs={3} item /> */}
                                    <Grid
                                        xs={6}
                                        item
                                        container
                                        sx={{
                                            flexDirection: "column",
                                            flexWrap: "nowrap",
                                            rowGap: "3px"
                                        }}
                                    >
                                        <Grid item>
                                            <Typography
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    lineHeight: "24px",
                                                    letterSpacing: "0.02px",
                                                    textAlign: "right",
                                                    color: theme.palette.text.primary
                                                })}
                                            >
                                                Market Value
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                variant="subtitle_500"
                                                sx={(theme: Theme) => ({
                                                    lineHeight: "21px",
                                                    letterSpacing: "0.02px",
                                                    textAlign: "right",
                                                    color: theme.palette.text.primary
                                                })}
                                            >
                                                {/* as on 30 Jan, 2024 */}
                                                as on {dayjs(businessDate, 'DD/MM/YYYY').format('DD MMM, YYYY')}
                                            </Typography>
                                        </Grid>
                                        <Grid item sx={{ marginTop: "8px" }}>
                                            <Typography
                                                variant="h1"
                                                sx={(theme: Theme) => ({
                                                    fontSize: { xs: "24px", sm: "26px", md: "28px", lg: "30px", xl: "32px" },
                                                    lineHeight: "40px",
                                                    color: theme.palette.primary[500],
                                                    textAlign: "right",
                                                    whiteSpace: "nowrap"
                                                })}
                                            >
                                                {formatCurrency({ value: familyData?.marketValue, locale, currencyCode })}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            container
                                            sx={{
                                                flexWrap: "nowrap",
                                                marginTop: "30px",
                                                columnGap: "30px",
                                                justifyContent: "flex-end",
                                                alignItems: "center"
                                            }}
                                        >
                                            <Grid
                                                xs="auto"
                                                item
                                                container
                                                sx={{
                                                    flexDirection: "column",
                                                    rowGap: "3px"
                                                }}
                                            >
                                                <Grid item>
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            whiteSpace: "nowrap",
                                                            textAlign: "right",
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.primary
                                                        })}
                                                    >
                                                        Total Investment
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography
                                                        variant="body_600"
                                                        sx={(theme: Theme) => ({
                                                            whiteSpace: "nowrap",
                                                            textAlign: "right",
                                                            lineHeight: "25px",
                                                            color: theme.palette.text.primary
                                                        })}
                                                    >
                                                        {formatCurrency({ value: familyData?.totalInvestment, locale, currencyCode })}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                xs="auto"
                                                item
                                                container
                                                sx={{
                                                    flexDirection: "column",
                                                    rowGap: "3px"
                                                }}
                                            >
                                                <Grid item>
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            whiteSpace: "nowrap",
                                                            textAlign: "right",
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.primary
                                                        })}
                                                    >
                                                        Returns
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography
                                                        variant="body_600"
                                                        sx={(theme: Theme) => ({
                                                            whiteSpace: "nowrap",
                                                            textAlign: "right",
                                                            lineHeight: "25px",
                                                            color: familyData?.returns >= 0 ? theme.palette.success[800] : theme.palette.error[500]
                                                        })}
                                                    >
                                                        {familyData?.returns > 0 ? '+' : ''} {formatCurrency({ value: familyData?.returns, locale, currencyCode })}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                xs="auto"
                                                item
                                                container
                                                sx={{
                                                    flexDirection: "column",
                                                    rowGap: "3px"
                                                }}
                                            >
                                                <Grid item>
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            whiteSpace: "nowrap",
                                                            textAlign: "right",
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.primary
                                                        })}
                                                    >
                                                        Absolute Return
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography
                                                        variant="body_600"
                                                        sx={(theme: Theme) => ({
                                                            whiteSpace: "nowrap",
                                                            textAlign: "right",
                                                            lineHeight: "25px",
                                                            color: familyData?.irr >= 0 ? theme.palette.success[800] : theme.palette.error[500]
                                                        })}
                                                    >
                                                        {familyData?.irr?.toFixed(1)}%
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid
                                    item
                                    container
                                    spacing={2}
                                >
                                    {familyData?.familyMembers?.slice(0, 2)?.map((member, index) => (

                                        <Grid
                                            item
                                            key={member?.name + index}
                                            xs={6}
                                        >
                                            <FamilyInfo
                                                columnWidth={{ xs: 12 }}
                                                personName={member?.name}
                                                isAdmin={member?.isAdmin}
                                                personDesignation={member?.relation}
                                                investmentValue={member?.investmentValue}
                                                currentValue={member?.currentValue}
                                                overallReturns={member?.overallReturns}
                                                irr={member?.irr}
                                                loading={isFamilyDataLoading}
                                                handleNavigation={handleClickEvent}
                                                clientCode={member.clientCode}
                                                investorType={member?.investorType ?? ""}
                                                isFamilyHead={member?.isFamilyHead ?? ""}
                                            />
                                        </Grid>
                                    ))}

                                </Grid>
                                <Grid
                                    item
                                    container
                                    spacing={2}
                                >
                                    {familyData?.familyMembers?.slice(2)?.map((member, index) => (

                                        <Grid
                                            item
                                            key={member?.name + index}
                                            xs={4}
                                        >
                                            <FamilyInfo
                                                key={member?.name + index}
                                                columnWidth={{ xs: 12 }}
                                                personName={member?.name}
                                                isAdmin={member?.isAdmin}
                                                personDesignation={member?.relation}
                                                investmentValue={member?.investmentValue}
                                                currentValue={member?.currentValue}
                                                overallReturns={member?.overallReturns}
                                                irr={member?.irr}
                                                loading={isFamilyDataLoading}
                                                handleNavigation={handleClickEvent}
                                                clientCode={member.clientCode}
                                                investorType={member?.investorType ?? ""}
                                                isFamilyHead={member?.isFamilyHead ?? ""}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                {familyData?.familyMembers?.length === 0 &&
                                    <Grid
                                        item
                                        sx={(theme: Theme) => ({
                                            backgroundColor: theme.palette.common.white,
                                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                            padding: '25px 30px 45px 30px',
                                            borderRadius: "20px",
                                            height: "fit-content"
                                        })}
                                    >
                                        <Typography variant="h3" textAlign={"center"}>No data found</Typography>
                                    </Grid>
                                }
                                {(apiError && !isSuccess) &&
                                    <Grid
                                        item
                                        sx={(theme: Theme) => ({
                                            backgroundColor: theme.palette.common.white,
                                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                            padding: '25px 30px 45px 30px',
                                            borderRadius: "20px",
                                            height: "fit-content"
                                        })}
                                    >
                                        <Typography variant="h3" textAlign={"center"}>Something is went wrong</Typography>
                                    </Grid>
                                }
                            </Grid>
                            <Grid
                                xs={4}
                                item
                            >
                                <Box
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                        padding: '25px 30px 45px 30px',
                                        borderRadius: "20px",
                                        height: "fit-content"
                                    })}
                                >

                                    <FamilyPieChartComponent
                                        title="Family Distribution"
                                        isLoading={isFamilyDataLoading}
                                        pieChartData={familyData?.familyDistribution?.data}
                                        pieChartLabelData={familyData?.familyDistribution?.labelData}
                                        pieChartTotalValue={familyData?.familyDistribution?.totalValue}
                                        open={open}
                                        setOpen={setOpen}
                                        apiError={apiError}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    }
                    {isFamilyDataLoading &&
                        <Grid
                            container
                            sx={{
                                marginTop: "25px",
                                flexWrap: "nowrap",
                                columnGap: "25px"
                            }}
                        >
                            <Grid
                                xs={8}
                                item
                                container
                                sx={{
                                    flexDirection: "column",
                                    rowGap: "25px",

                                }}
                            >
                                <Grid
                                    item
                                    container
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                        padding: "35px",
                                        borderRadius: "25px",
                                        flexWrap: "nowrap",
                                        justifyContent: "space-between",

                                    })}
                                >
                                    <Grid
                                        xs={6}
                                        item
                                        container
                                        sx={{
                                            columnGap: "15px",
                                            alignItems: "flex-start",
                                            flexWrap: "nowrap",
                                            wordBreak: "break-word"
                                        }}
                                    >
                                        <Grid item>
                                            <Skeleton animation="wave" variant="circular" width={50} height={50} />
                                        </Grid>
                                        <Grid
                                            item
                                            container
                                            sx={{
                                                flexDirection: "column",
                                                rowGap: "3px",
                                                flexWrap: "nowrap"
                                            }}
                                        >
                                            <Grid item>
                                                <Skeleton animation="wave" />
                                            </Grid>
                                            <Grid item>
                                                <Skeleton animation="wave" width={'50%'} />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid
                                        xs={6}
                                        item
                                        container
                                        sx={{
                                            flexDirection: "column",
                                            flexWrap: "nowrap",
                                            rowGap: "3px",
                                            alignItems: 'flex-end'
                                        }}
                                    >
                                        <Grid item>
                                            <Skeleton animation="wave" width={'120px'} />
                                        </Grid>
                                        <Grid item>
                                            <Skeleton animation="wave" width={'140px'} />
                                        </Grid>
                                        <Grid item sx={{ marginTop: "8px" }}>
                                            <Skeleton animation="wave" width={'170px'} height="30px" />
                                        </Grid>
                                        <Grid
                                            item
                                            container
                                            sx={{
                                                flexWrap: "nowrap",
                                                marginTop: "30px",
                                                columnGap: "30px",
                                                justifyContent: "flex-end",
                                                alignItems: "center"
                                            }}
                                        >
                                            <Grid
                                                xs="auto"
                                                item
                                                container
                                                sx={{
                                                    flexDirection: "column",
                                                    rowGap: "3px"
                                                }}
                                            >
                                                <Grid item>
                                                    <Skeleton animation="wave" />
                                                </Grid>
                                                <Grid item>
                                                    <Skeleton animation="wave" width={'80px'} />
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                xs="auto"
                                                item
                                                container
                                                sx={{
                                                    flexDirection: "column",
                                                    rowGap: "3px"
                                                }}
                                            >
                                                <Grid item>
                                                    <Skeleton animation="wave" />
                                                </Grid>
                                                <Grid item>
                                                    <Skeleton animation="wave" width={'80px'} />
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                xs="auto"
                                                item
                                                container
                                                sx={{
                                                    flexDirection: "column",
                                                    rowGap: "3px"
                                                }}
                                            >
                                                <Grid item>
                                                    <Skeleton animation="wave" />
                                                </Grid>
                                                <Grid item>
                                                    <Skeleton animation="wave" width={'80px'} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid
                                    item
                                    container
                                    sx={{
                                        flexWrap: "nowrap",
                                        columnGap: "25px"
                                    }}
                                >
                                    <FamilyInfo
                                        columnWidth={{ xs: 6 }}
                                        personName="Rahul Kumar"
                                        isAdmin
                                        personDesignation="Head of family"
                                        investmentValue={2222000}
                                        currentValue={2432000}
                                        overallReturns={122000}
                                        irr={25.56}
                                        loading={isFamilyDataLoading}
                                    />
                                    <FamilyInfo
                                        columnWidth={{ xs: 6 }}
                                        personName="Bhuveshwar Kumar"
                                        personDesignation="Son"
                                        investmentValue={2222000}
                                        currentValue={122000}
                                        overallReturns={122000}
                                        irr={25.56}
                                        loading={isFamilyDataLoading}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    container
                                    sx={{
                                        flexWrap: "nowrap",
                                        columnGap: "25px"
                                    }}
                                >
                                    <FamilyInfo
                                        columnWidth={{ xs: 4 }}
                                        personName="Ravi Kumar"
                                        personDesignation="Son"
                                        investmentValue={1111111}
                                        currentValue={1111111}
                                        overallReturns={1111111}
                                        irr={21.35}
                                        loading={isFamilyDataLoading}
                                    />
                                    <FamilyInfo
                                        columnWidth={{ xs: 4 }}
                                        personName="Bhuvi Kumar"
                                        personDesignation="Son"
                                        investmentValue={1111111}
                                        currentValue={1111111}
                                        overallReturns={1111111}
                                        irr={21.35}
                                        loading={isFamilyDataLoading}
                                    />
                                    <FamilyInfo
                                        columnWidth={{ xs: 4 }}
                                        personName="Neha Kumari"
                                        personDesignation="Daughter"
                                        investmentValue={1111111}
                                        currentValue={1111111}
                                        overallReturns={1111111}
                                        irr={21.35}
                                        loading={isFamilyDataLoading}
                                    />
                                </Grid>

                            </Grid>
                            <Grid
                                xs={4}
                                item
                            >
                                <Box
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                        padding: '25px 30px 45px 30px',
                                        borderRadius: "20px",
                                        height: "fit-content"
                                    })}
                                >
                                    <FamilyPieChartComponent
                                        title="Family Distribution"
                                        isLoading={isFamilyDataLoading}
                                        pieChartData={familyData?.familyDistribution?.data}
                                        pieChartLabelData={familyData?.familyDistribution?.labelData}
                                        pieChartTotalValue={familyData?.familyDistribution?.totalValue}
                                        open={open}
                                        setOpen={setOpen}
                                        apiError={apiError}
                                    />
                                </Box>
                            </Grid>

                        </Grid>
                    }
                </Hidden>
                <Hidden lgUp>
                    {!isFamilyDataLoading &&
                        <>
                            <Grid
                                container
                                sx={{
                                    marginTop: "25px",
                                    flexDirection: "column",
                                    rowGap: "25px"
                                }}
                            >
                                <Grid
                                    xs={12}
                                    item
                                    container
                                    sx={{
                                        flexDirection: "column",
                                        rowGap: "25px",

                                    }}
                                >
                                    <Grid
                                        item
                                        container
                                        sx={(theme: Theme) => ({
                                            backgroundColor: theme.palette.common.white,
                                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                            padding: "35px",
                                            borderRadius: "25px",
                                            flexWrap: "nowrap",
                                            justifyContent: "space-between",

                                        })}
                                    >
                                        <Grid
                                            xs={6}
                                            item
                                            container
                                            sx={{
                                                columnGap: "15px",
                                                alignItems: "flex-start",
                                                flexWrap: "nowrap",
                                                wordBreak: "break-word"
                                            }}
                                        >
                                            <Grid item>
                                                <Avatar
                                                    sx={(theme: Theme) => ({
                                                        backgroundColor: theme.palette.primary[50],
                                                        padding: { xs: "15px", sm: "18px", md: "20px", lg: "25px" }
                                                    })}
                                                >
                                                    <Typography
                                                        variant="h2"
                                                        sx={(theme: Theme) => ({
                                                            color: theme.palette.primary[500]
                                                        })}
                                                    >
                                                        {familyData?.familyName?.[0]}
                                                    </Typography>
                                                </Avatar>
                                            </Grid>
                                            <Grid
                                                item
                                                container
                                                sx={{
                                                    flexDirection: "column",
                                                    rowGap: "3px",
                                                    flexWrap: "nowrap"
                                                }}
                                            >
                                                {(apiError && !isSuccess) && <Typography variant="body_500">Something is went wrong</Typography>}
                                                <Grid item>
                                                    <Typography
                                                        variant="h3"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "32.5px",
                                                            color: theme.palette.text.primary
                                                        })}
                                                    >
                                                        {familyData?.familyName?.split(" ")[0]}
                                                    </Typography>
                                                </Grid>

                                                <Grid item>
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.secondary
                                                        })}
                                                    >
                                                        {familyData?.familyName?.split(" ")[1] || ''}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            xs={6}
                                            item
                                            container
                                            sx={{
                                                flexDirection: "column",
                                                flexWrap: "nowrap",
                                                rowGap: "3px"
                                            }}
                                        >
                                            <Grid item>
                                                <Typography
                                                    variant="body_500"
                                                    sx={(theme: Theme) => ({
                                                        lineHeight: "24px",
                                                        letterSpacing: "0.02px",
                                                        textAlign: "right",
                                                        color: theme.palette.text.primary
                                                    })}
                                                >
                                                    Market Value
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography
                                                    variant="subtitle_500"
                                                    sx={(theme: Theme) => ({
                                                        lineHeight: "21px",
                                                        letterSpacing: "0.02px",
                                                        textAlign: "right",
                                                        color: theme.palette.text.primary
                                                    })}
                                                >
                                                    {/* as on 30 Jan, 2024 */}
                                                    as on {dayjs(businessDate, 'DD/MM/YYYY').format('DD MMM, YYYY')}
                                                </Typography>
                                            </Grid>
                                            <Grid item sx={{ marginTop: "8px" }}>
                                                <Typography
                                                    variant="h1"
                                                    sx={(theme: Theme) => ({
                                                        fontSize: { xs: "24px", sm: "26px", md: "28px", lg: "30px", xl: "32px" },
                                                        lineHeight: "40px",
                                                        color: theme.palette.primary[500],
                                                        textAlign: "right",
                                                        whiteSpace: "nowrap"
                                                    })}
                                                >
                                                    {formatCurrency({ value: familyData?.marketValue, locale, currencyCode })}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                item
                                                container
                                                sx={{
                                                    flexWrap: "nowrap",
                                                    marginTop: "30px",
                                                    columnGap: "30px",
                                                    justifyContent: "flex-end",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <Grid
                                                    xs="auto"
                                                    item
                                                    container
                                                    sx={{
                                                        flexDirection: "column",
                                                        rowGap: "3px"
                                                    }}
                                                >
                                                    <Grid item>
                                                        <Typography
                                                            variant="body_500"
                                                            sx={(theme: Theme) => ({
                                                                whiteSpace: "nowrap",
                                                                textAlign: "right",
                                                                lineHeight: "20px",
                                                                letterSpacing: "0.02px",
                                                                color: theme.palette.text.primary
                                                            })}
                                                        >
                                                            Total Investment
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography
                                                            variant="body_600"
                                                            sx={(theme: Theme) => ({
                                                                whiteSpace: "nowrap",
                                                                textAlign: "right",
                                                                lineHeight: "25px",
                                                                color: theme.palette.text.primary
                                                            })}
                                                        >
                                                            {formatCurrency({ value: familyData?.totalInvestment, locale, currencyCode })}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid
                                                    xs="auto"
                                                    item
                                                    container
                                                    sx={{
                                                        flexDirection: "column",
                                                        rowGap: "3px"
                                                    }}
                                                >
                                                    <Grid item>
                                                        <Typography
                                                            variant="body_500"
                                                            sx={(theme: Theme) => ({
                                                                whiteSpace: "nowrap",
                                                                textAlign: "right",
                                                                lineHeight: "20px",
                                                                letterSpacing: "0.02px",
                                                                color: theme.palette.text.primary
                                                            })}
                                                        >
                                                            Returns
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography
                                                            variant="body_600"
                                                            sx={(theme: Theme) => ({
                                                                whiteSpace: "nowrap",
                                                                textAlign: "right",
                                                                lineHeight: "25px",
                                                                color: familyData?.returns > 0 ? theme.palette.success[800] : theme.palette.error[500]
                                                            })}
                                                        >
                                                            {familyData?.returns > 0 ? '+' : ''} {formatCurrency({ value: familyData?.returns, locale, currencyCode })}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid
                                                    xs="auto"
                                                    item
                                                    container
                                                    sx={{
                                                        flexDirection: "column",
                                                        rowGap: "3px"
                                                    }}
                                                >
                                                    <Grid item>
                                                        <Typography
                                                            variant="body_500"
                                                            sx={(theme: Theme) => ({
                                                                whiteSpace: "nowrap",
                                                                textAlign: "right",
                                                                lineHeight: "20px",
                                                                letterSpacing: "0.02px",
                                                                color: theme.palette.text.primary
                                                            })}
                                                        >
                                                            Absolute Return
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography
                                                            variant="body_600"
                                                            sx={(theme: Theme) => ({
                                                                whiteSpace: "nowrap",
                                                                textAlign: "right",
                                                                lineHeight: "25px",
                                                                color: familyData?.irr > 0 ? theme.palette.success[800] : theme.palette.error[500]
                                                            })}
                                                        >
                                                            {familyData?.irr?.toFixed(1)}%
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <Box
                                        sx={(theme: Theme) => ({
                                            backgroundColor: theme.palette.common.white,
                                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                            padding: '25px 30px 45px 30px',
                                            borderRadius: "20px",
                                            height: "fit-content"
                                        })}
                                    >
                                        <FamilyPieChartComponent
                                            title="Family Distribution"
                                            isLoading={isFamilyDataLoading}
                                            pieChartData={familyData?.familyDistribution?.data}
                                            pieChartLabelData={familyData?.familyDistribution?.labelData}
                                            pieChartTotalValue={familyData?.familyDistribution?.totalValue}
                                            open={open}
                                            setOpen={setOpen}
                                            apiError={apiError}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid container sx={{ marginTop: "25px", gap: "25px" }}>
                                {familyData?.familyMembers?.slice(0, 2)?.map((member, index) => (

                                    <FamilyInfo
                                        key={member?.name + index}
                                        columnWidth={{ xs: 12 }}
                                        personName={member?.name}
                                        isAdmin={member?.isAdmin}
                                        personDesignation={member?.relation}
                                        investmentValue={member?.investmentValue}
                                        currentValue={member?.currentValue}
                                        overallReturns={member?.overallReturns}
                                        irr={member?.irr}
                                        loading={isFamilyDataLoading}
                                        handleNavigation={handleClickEvent}
                                        clientCode={member.clientCode}
                                        investorType={member?.investorType ?? ""}
                                        isFamilyHead={member?.isFamilyHead ?? ""}
                                    />
                                ))}
                            </Grid>
                            <Grid container sx={{ marginTop: "25px", gap: "25px" }}>
                                {familyData?.familyMembers?.slice(2)?.map((member, index) => (

                                    <FamilyInfo
                                        key={member?.name + index}
                                        columnWidth={{ xs: 12 }}
                                        personName={member?.name}
                                        isAdmin={member?.isAdmin}
                                        personDesignation={member?.relation}
                                        investmentValue={member?.investmentValue}
                                        currentValue={member?.currentValue}
                                        overallReturns={member?.overallReturns}
                                        irr={member?.irr}
                                        loading={isFamilyDataLoading}
                                        handleNavigation={handleClickEvent}
                                        clientCode={member.clientCode}
                                        investorType={member?.investorType ?? ""}
                                        isFamilyHead={member?.isFamilyHead ?? ""}
                                    />
                                ))}
                            </Grid>
                            {(apiError && !isSuccess) &&
                                <Grid
                                    container
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                        padding: '25px 30px 45px 30px',
                                        borderRadius: "20px",
                                        height: "fit-content"
                                    })}
                                >
                                    <Typography variant="h3" textAlign={"center"}>Something is went wrong</Typography>
                                </Grid>
                            }
                            {familyData?.familyMembers?.length === 0 &&
                                <Grid
                                    item
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                        padding: '25px 30px 45px 30px',
                                        borderRadius: "20px",
                                        height: "fit-content"
                                    })}
                                >
                                    <Typography variant="h3" textAlign={"center"}>No data found</Typography>
                                </Grid>
                            }
                        </>
                    }
                    {
                        isFamilyDataLoading &&
                        <>
                            <Grid
                                container
                                sx={{
                                    marginTop: "25px",
                                    flexDirection: "column",
                                    rowGap: "25px"
                                }}
                            >
                                <Grid
                                    xs={12}
                                    item
                                    container
                                    sx={{
                                        flexDirection: "column",
                                        rowGap: "25px",

                                    }}
                                >
                                    <Grid
                                        item
                                        container
                                        sx={(theme: Theme) => ({
                                            backgroundColor: theme.palette.common.white,
                                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                            padding: "35px",
                                            borderRadius: "25px",
                                            flexWrap: "nowrap",
                                            justifyContent: "space-between",

                                        })}
                                    >
                                        <Grid
                                            xs={6}
                                            item
                                            container
                                            sx={{
                                                columnGap: "15px",
                                                alignItems: "flex-start",
                                                flexWrap: "nowrap",
                                                wordBreak: "break-word"
                                            }}
                                        >
                                            <Grid item>
                                                <Skeleton animation='wave' variant="circular" width={50} height={50} />
                                            </Grid>
                                            <Grid
                                                item
                                                container
                                                sx={{
                                                    flexDirection: "column",
                                                    rowGap: "3px",
                                                    flexWrap: "nowrap"
                                                }}
                                            >
                                                <Grid item>
                                                    <Skeleton animation="wave" width={'80%'} />
                                                </Grid>
                                                <Grid item>
                                                    <Skeleton animation="wave" width={'40%'} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            xs={6}
                                            item
                                            container
                                            sx={{
                                                flexDirection: "column",
                                                flexWrap: "nowrap",
                                                rowGap: "3px",
                                                alignItems: 'flex-end'
                                            }}
                                        >
                                            <Grid item>
                                                <Skeleton animation="wave" width={'120px'} />
                                            </Grid>
                                            <Grid item>
                                                <Skeleton animation="wave" width={'140px'} />
                                            </Grid>
                                            <Grid item sx={{ marginTop: "8px" }}>
                                                <Skeleton animation="wave" width={'160px'} height="30px" />
                                            </Grid>
                                            <Grid
                                                item
                                                container
                                                sx={{
                                                    flexWrap: "nowrap",
                                                    marginTop: "30px",
                                                    columnGap: "30px",
                                                    justifyContent: "flex-end",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <Grid
                                                    xs="auto"
                                                    item
                                                    container
                                                    sx={{
                                                        flexDirection: "column",
                                                        rowGap: "3px"
                                                    }}
                                                >
                                                    <Grid item>
                                                        <Skeleton animation="wave" width={'80px'} />
                                                    </Grid>
                                                    <Grid item>
                                                        <Skeleton animation="wave" width={'80px'} />
                                                    </Grid>
                                                </Grid>
                                                <Grid
                                                    xs="auto"
                                                    item
                                                    container
                                                    sx={{
                                                        flexDirection: "column",
                                                        rowGap: "3px"
                                                    }}
                                                >
                                                    <Grid item>
                                                        <Skeleton animation="wave" width={'80px'} />
                                                    </Grid>
                                                    <Grid item>
                                                        <Skeleton animation="wave" width={'80px'} />
                                                    </Grid>
                                                </Grid>
                                                <Grid
                                                    xs="auto"
                                                    item
                                                    container
                                                    sx={{
                                                        flexDirection: "column",
                                                        rowGap: "3px"
                                                    }}
                                                >
                                                    <Grid item>
                                                        <Skeleton animation="wave" width={'80px'} />
                                                    </Grid>
                                                    <Grid item>
                                                        <Skeleton animation="wave" width={'80px'} />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid
                                    xs={12}
                                    item
                                >
                                    <Box
                                        sx={(theme: Theme) => ({
                                            backgroundColor: theme.palette.common.white,
                                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                            padding: '25px 30px 45px 30px',
                                            borderRadius: "20px",
                                            height: "fit-content"
                                        })}
                                    >
                                        <Typography
                                            variant="h2"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "25px",
                                                letterSpacing: "0.01px",
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            Family Distribution
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid container sx={{ marginTop: "25px", gap: "25px" }}>
                                <FamilyInfo
                                    columnWidth={{ xs: 12 }}
                                    personName="Rahul Kumar"
                                    isAdmin
                                    personDesignation="Head of family"
                                    investmentValue={123456}
                                    currentValue={123456}
                                    overallReturns={123456}
                                    irr={12.3}
                                    loading={isFamilyDataLoading}
                                />
                                <FamilyInfo
                                    columnWidth={{ xs: 12 }}
                                    personName="Bhuveshwar Kumar"
                                    personDesignation="Son"
                                    investmentValue={123456}
                                    currentValue={123456}
                                    overallReturns={123456}
                                    irr={12.3}
                                    loading={isFamilyDataLoading}
                                />
                            </Grid>
                            <Grid container sx={{ marginTop: "25px", gap: "25px" }}>
                                <FamilyInfo
                                    columnWidth={{ xs: 12 }}
                                    personName="Ravi Kumar"
                                    personDesignation="Son"
                                    investmentValue={123456}
                                    currentValue={123456}
                                    overallReturns={123456}
                                    irr={12.3}
                                    loading={isFamilyDataLoading}
                                />
                                <FamilyInfo
                                    columnWidth={{ xs: 12 }}
                                    personName="Bhuvi Kumar"
                                    personDesignation="Son"
                                    investmentValue={123456}
                                    currentValue={123456}
                                    overallReturns={123456}
                                    irr={12.3}
                                    loading={isFamilyDataLoading}
                                />
                                <FamilyInfo
                                    columnWidth={{ xs: 12 }}
                                    personName="Neha Kumari"
                                    personDesignation="Daughter"
                                    investmentValue={123456}
                                    currentValue={123456}
                                    overallReturns={123456}
                                    irr={12.3}
                                    loading={isFamilyDataLoading}
                                />
                            </Grid>
                        </>
                    }

                </Hidden>

            </>
        </CommonLayout >
    )
}
